'use client'

import Image from 'next/image'
import type { ReactNode, RefObject } from 'react'
import { createRef } from 'react'

import type { Heading } from 'datocms-structured-text-utils'
import { isHeading, renderRule } from 'datocms-structured-text-utils'
import { StructuredText } from 'react-datocms/structured-text'
import { TableOfContents } from './TableOfContents'
import type {
	ExternalVideoComponentFragment,
	ImageComponentFragment,
	ImageFragment,
	InternalVideoComponentFragment,
	MetricsSectionFragment,
} from '@/lib/types/datocms/graphql/generated'
import MetricsSection from './sections/MetricsSection'
import SocialShare from './social-media/SocialShare'
import InternalVideoComponent from './InternalVideoComponent'
import ExternalVideoComponent from './sections/ExternalVideoComponent'
import Carousel from './Carousel'
import clsx from 'clsx'

type Props = {
	structuredText: any
	author?: any
}

export default function StructuredTextWithSidebar({ structuredText, author }: Readonly<Props>) {
	const headings: Heading[] = structuredText?.value.document.children.filter((section: Heading) => {
		return section.type === 'heading' && section.level === 2
	})

	const headingRefs = headings.reduce(
		(refs: Record<string, RefObject<HTMLHeadingElement>>, headingRef) => {
			if (headingRef.children.length) {
				const id = headingRef.children.map((child) => child.type === 'span' && child.value).join('')

				if (id) {
					refs[id] = createRef<HTMLHeadingElement>()
				}
			}

			return refs
		},
		{}
	)

	const headingRender = (children: ReactNode, node: any) => {
		const id = node.children?.map((child: any) => child.value).join('')
		const Tag: any = `h${node.level}`

		return (
			<Tag key={id} id={id} ref={headingRefs[id]}>
				{children}
			</Tag>
		)
	}

	return (
		<section className="md:bg-white-frosted">
			<div className="relative grid grid-cols-3 max-w-screen-2xl mx-auto">
				<article
					className="max-md:order-2 rich-text col-span-3 md:col-span-2 bg-white px-3 pt-4 pb-16 md:p-16 lg:p-24 md:-translate-y-16 md:rounded-r-xl 2xl:rounded-xl"
					data-testid="rich-text-content"
				>
					{/* Author */}
					{author?.image?.[0]?.url && (
						<div className="flex items-center gap-4 mb-10">
							{author?.image?.[0]?.url && (
								<div className="w-10 h-10 rounded-md overflow-hidden">
									<Image
										src={author?.image?.[0]?.url}
										alt={author?.image?.[0]?.alt ?? 'Author'}
										width={48}
										height={48}
										className="object-cover w-full h-full"
										sizes="40px"
									/>
								</div>
							)}
							<span className="font-mono text-sm text-blue-dark">{author?.name}</span>
						</div>
					)}

					<StructuredText
						data={structuredText}
						customNodeRules={[
							renderRule(isHeading, ({ children, node }) => headingRender(children, node)),
						]}
						renderBlock={({ record }) => {
							if (record.__typename === 'ImageBlockRecord') {
								const imageComponent = record as ImageComponentFragment
								if (imageComponent?.image?.length === 1) {
									return (
										<Image
											src={imageComponent?.image?.[0]?.url}
											alt={imageComponent?.image?.[0]?.alt ?? 'Structured text image'}
											width={imageComponent?.image?.[0]?.width}
											height={imageComponent?.image?.[0]?.height}
											data-testid="rich-text-image"
											className="rounded-xl"
											sizes="90vw"
										/>
									)
								}

								if (imageComponent?.image?.length === 2) {
									return (
										<div className="image-container grid grid-cols-2 gap-4">
											{imageComponent?.image?.map((image: ImageFragment) => {
												return (
													<Image
														key={image.id as string}
														src={image.url}
														alt={image.title ?? 'blog image'}
														width={image.width as number}
														height={image.height as number}
														className="rounded-xl"
														sizes="90vw"
													/>
												)
											})}
										</div>
									)
								}

								return (
									<Carousel className="image-container mt-12 rounded-xl overflow-hidden @container">
										{imageComponent?.image?.length
											? imageComponent?.image?.map((image: ImageFragment, idx: number) => {
													if (!image.url) {
														return null
													}
													return (
														<div
															className="carousel-card h-[460px] min-w-[342px] rounded-lg overflow-hidden bg-white-frosted"
															key={image.id as string}
														>
															<Image
																key={`${image.id}-${idx}`}
																src={image.url}
																alt={image.title ?? 'Carousel image'}
																width={100}
																height={100}
																sizes="80vw"
																className={clsx('object-cover h-full w-full')}
															/>
														</div>
													)
												})
											: null}
									</Carousel>
								)
							}
							if (record.__typename === 'MetricsSectionRecord') {
								return <MetricsSection {...(record as MetricsSectionFragment)} />
							}
							if (record.__typename === 'InternalVideoComponentRecord') {
								return <InternalVideoComponent {...(record as InternalVideoComponentFragment)} />
							}
							if (record.__typename === 'ExternalVideoComponentRecord') {
								return <ExternalVideoComponent {...(record as ExternalVideoComponentFragment)} />
							}

							return null
						}}
						renderLinkToRecord={() => {
							return null
						}}
						renderInlineRecord={() => {
							return null
						}}
					/>

					<SocialShare className="md:hidden mt-16" />
				</article>

				{/* Table of Contents */}
				<aside className="max-md:col-span-3">
					<div className="sticky top-16 pt-16 px-16  mb-20">
						{headings?.length > 0 && (
							<>
								<TableOfContents headings={headings} refs={headingRefs} />
								<hr className="mb-6" />
							</>
						)}
						<SocialShare className="max-md:hidden" />
					</div>
				</aside>
			</div>
		</section>
	)
}
